import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useProfileQuery } from '../features/account/use-profile.ts';
import { useAuth } from './auth.tsx';
import { queryKeys } from './queryKeys.ts';
import { supabase } from './supabase.ts';
import {
  applyTheme,
  browserStorage,
  DARK_QUERY,
  parsePreference,
  readStoredPreference,
  resolveTheme,
  systemPrefersDark,
  type Theme,
  type ThemePreference,
  writeStoredPreference,
} from './theme.ts';

export interface ThemeApi {
  preference: ThemePreference;
  /** What is on screen now. */
  theme: Theme;
  setPreference: (next: ThemePreference) => void;
  /** Saving to the account failed; the choice still applies on this device. */
  saveError: string | null;
}

const ThemeContext = createContext<ThemeApi | null>(null);

/**
 * Applies the theme and keeps it in sync: the local copy first (index.html already painted it), then
 * the account's choice once the profile loads after sign-in, so the choice follows the user across
 * devices. A change applies at once, then saves to both.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const { claims } = useAuth();
  const userId = claims?.userId ?? null;
  const queryClient = useQueryClient();
  const [preference, setPreferenceState] = useState(() => readStoredPreference(browserStorage()));
  const [systemDark, setSystemDark] = useState(systemPrefersDark);
  const theme = resolveTheme(preference, systemDark);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = () => setSystemDark(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Adopt the account's choice once per signed-in user, unless they changed it here meanwhile.
  const profile = useProfileQuery(userId);
  const adoptedFor = useRef<string | null>(null);
  const changedFor = useRef<string | null>(null);
  const accountPreference = profile.data?.theme_preference;
  useEffect(() => {
    if (!userId || accountPreference === undefined || adoptedFor.current === userId) return;
    adoptedFor.current = userId;
    if (changedFor.current === userId) return;
    const next = parsePreference(accountPreference);
    setPreferenceState(next);
    writeStoredPreference(browserStorage(), next);
  }, [userId, accountPreference]);

  const save = useMutation({
    // One save at a time, in order: quick changes (arrow keys) must not land out of order.
    scope: { id: 'theme-preference' },
    mutationFn: async ({ id, next }: { id: string; next: ThemePreference }) => {
      const { error } = await supabase()
        .from('profiles')
        .update({ theme_preference: next })
        .eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSettled: (_data, _error, { id }) =>
      queryClient.invalidateQueries({ queryKey: queryKeys.profile(id) }),
  });
  const { mutate } = save;

  const setPreference = useCallback(
    (next: ThemePreference) => {
      changedFor.current = userId;
      setPreferenceState(next);
      writeStoredPreference(browserStorage(), next);
      if (userId) mutate({ id: userId, next });
    },
    [userId, mutate],
  );

  const saveError = save.isError
    ? "Couldn't save it to your account. It's saved on this device."
    : null;
  const value = useMemo(
    () => ({ preference, theme, setPreference, saveError }),
    [preference, theme, setPreference, saveError],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** The theme on screen, also outside the provider (the not-configured and crash screens). */
export function useResolvedTheme(): Theme {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx.theme;
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

export function useTheme(): ThemeApi {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
